import * as helpers from 'helpers';
import {Sorting} from 'helpers/constants';

import {asyncLoad} from './.';
import request from '../api/request';
import * as alerts from './alertActions';

const DROPDOWN_FIELDS = 'customerId,name';
const DROPDOWN_FIELDS_SL = 'fullAddress,serviceLocationId,name';

const loadCustomersMaster = ({
  customerId = null,
  fields = '',
  searchValue = null,
  serviceLocations = false,
  serviceLocationId = null,
  sortColumn = 'name',
  sortDirection = Sorting.asc,
}) => dispatch =>
  asyncLoad({
    dispatch,
    title: serviceLocations ? 'service locations' : 'customers',
    url: `/customers${customerId ? `/${customerId}` : ''}${
      serviceLocations ? '/servicelocations' : ''
    }${serviceLocationId ? `/${serviceLocationId}` : ''}${
      fields ? `?fields=${fields}` : ''
    }${searchValue ? `${fields ? '&' : '?'}name=${searchValue}` : ''}${
      sortColumn && (!customerId || serviceLocations) && !serviceLocationId
        ? `${fields || searchValue ? '&' : '?'}sort=${
            sortDirection === Sorting.desc ? '-' : ''
          }${sortColumn}`
        : ''
    }`,
  });

export const loadSpecificCustomers = customerIds => (dispatch, getState) =>
  asyncLoad({
    dispatch,
    title: getState().portal.dictionary.CustomerName.pluralValue,
    url: `/customers/bulk?customerIds=${customerIds.join('&customerIds=')}`,
  });

export const loadCustomers = (
  searchValue = null,
  dropdown = false,
  sortColumn = 'name',
  sortDirection = Sorting.asc,
) => dispatch =>
  dispatch(
    loadCustomersMaster({
      fields: dropdown ? DROPDOWN_FIELDS : '',
      searchValue,
      sortColumn,
    }),
  );

export const loadCustomersTable = ({
  customViewId = null,
  pageInfo = {},
  sortInfo = {},
  name = undefined,
  showServiceLocations = false,
}) => dispatch => {
  const params = {
    pageSize: pageInfo.pageSize,
    rowIndex: helpers.rowIndexFromPage(pageInfo),
    sort: helpers.sortParam(sortInfo),
    showServiceLocations,
  };
  if (name) {
    params.name = name;
  }
  if (customViewId) {
    params.customViewId = customViewId;
  }
  return asyncLoad({
    dispatch,
    title: 'customers',
    url: `/customers?${helpers.objectToParams(params)}`,
  });
};

export const loadCustomer = (customerId, dropdown = false) => dispatch =>
  dispatch(
    loadCustomersMaster({
      fields: dropdown ? DROPDOWN_FIELDS : '',
      customerId,
    }),
  );

export const loadServiceLocations = (
  customerId,
  dropdown = false,
  searchValue = null,
) => dispatch =>
  dispatch(
    loadCustomersMaster({
      fields: dropdown ? DROPDOWN_FIELDS_SL : '',
      customerId,
      searchValue,
      serviceLocations: true,
    }),
  );

export const loadServiceLocation = (
  customerId,
  serviceLocationId,
  dropdown = false,
) => dispatch =>
  dispatch(
    loadCustomersMaster({
      fields: dropdown ? DROPDOWN_FIELDS : '',
      customerId,
      serviceLocations: true,
      serviceLocationId,
    }),
  );

export const loadContacts = (
  {customerId = null, serviceLocationId = null},
  searchValue = null,
) => async dispatch => {
  const contacts = customerId
    ? await asyncLoad({
        dispatch,
        title: 'contacts',
        url: `/customers/${customerId}/contacts?sort=firstname${
          searchValue ? `&name=${searchValue}` : ''
        }`,
      })
    : await asyncLoad({
        dispatch,
        title: 'contacts',
        url: `/servicelocations/${serviceLocationId}/contacts?showParentContacts=true&sort=firstname${
          searchValue ? `&name=${searchValue}` : ''
        }`,
      });

  contacts.results.unshift({contactId: 0, firstname: '[None]'});
  contacts.totalRows = contacts.totalRows + 1;

  return contacts;
};

export const loadContact = contactId => dispatch =>
  asyncLoad({
    dispatch,
    title: 'contact',
    url: `/contacts/${contactId}`,
  });

export const exportCsv = ({
  customViewId = null,
  showServiceLocations = null,
}) => async dispatch => {
  try {
    const result = await request({
      url: `/csv/customers?${
        customViewId ? `CustomViewId=${customViewId}` : ''
      }${
        showServiceLocations !== null
          ? `&showServiceLocations=${showServiceLocations}`
          : ''
      }`,
      headers: {contentType: 'text/csv'},
    });

    if (result.status === 200) {
      // Check if the response has a warning status
      if (result.data && result.data.status === 'warning') {
        dispatch(alerts.warning(result.data.message));
        return true;
      }
      dispatch(alerts.success(result.data.message));
      return true;
    }
    throw new Error(
      result && result.data && result.data.error && result.data.error.message,
    );
  } catch (error) {
    helpers.handleError('Error exporting to CSV', error, dispatch);
    return false;
  }
};

export const bulkDelete = customers => async dispatch => {
  return request({
    url: `/customers/`,
    body: {
      Operation: 'delete',
      Id: null,
      Values: customers.map(c => ({
        customerId: c.customerId,
      })),
    },
    method: 'PUT',
    batch: true,
  })
    .then(response => {
      if (!response || !response.data || !response.data.hasErrors) {
        dispatch(alerts.success('Delete successful'));
        return true;
      } else if (response && response.data && response.data.results) {
        response.data.results.forEach(i => {
          if (!i.success) {
            dispatch(alerts.error(`Error: ${i.errorMessage}`));
          }
        });
      }
      return false;
    })
    .catch(error => {
      helpers.handleError('Unable to delete', error, dispatch);
      return false;
    });
};

export const getCustomerEmailList = customerId => dispatch =>
  asyncLoad({
    dispatch,
    title: 'email list',
    url: `/customers/${customerId}/email-list`,
  });

export const getBillToList = customerId => async (dispatch, getState) => {
  const customerKeyword = getState().portal.dictionary.CustomerName.value;
  const contactKeyword = getState().portal.dictionary.CustomerContact.value;
  const list = [];

  const customer = await asyncLoad({
    dispatch,
    url: `/customers/${customerId}`,
  });
  const isServiceLocation = customer.parentId !== null;
  const billToParent = isServiceLocation ? customer.billToParent : false;

  if (isServiceLocation && billToParent) {
    const parent = await asyncLoad({
      dispatch,
      url: `/customers/${customer.parentId}`,
    });

    list.push({
      text: `${parent.name.replace(/(\s?ID_(\d)+)/, '')} [${customerKeyword}] ${
        parent.isActive ? '' : '(Deleted)'
      }`,
      type: 'Customer',
      value: parent.customerId,
    });
  } else {
    list.push({
      text: `${customer.name.replace(
        /(\s?ID_(\d)+)/,
        '',
      )} [${customerKeyword}]  ${customer.isActive ? '' : '(Deleted)'}`,
      type: isServiceLocation ? 'ServiceLocation' : 'Customer',
      value: customer.customerId,
    });
  }

  const contacts = await asyncLoad({
    dispatch,
    url: `/${
      isServiceLocation && !billToParent ? 'servicelocations' : 'customers'
    }/${
      isServiceLocation
        ? billToParent
          ? customer.parentId
          : customerId
        : customerId
    }/contacts`,
  });

  contacts.results.forEach(c =>
    list.push({
      text: `${helpers.getContactName(c, contactKeyword)} [${contactKeyword}]`,
      type: 'Contact',
      value: c.contactId,
    }),
  );

  return list;
};

export const loadCustomerTypes = (name = '') => async dispatch =>
  asyncLoad({
    dispatch,
    title: 'Types',
    url: `/customer-types?name=${name}`,
  });

export const loadTerms = () => async dispatch =>
  asyncLoad({
    dispatch,
    title: 'Terms',
    url: `/terms?pageSize=500`,
  });



// WEBPACK FOOTER //
// ./src/actions/customerActions.js